import { Fragment, PropsWithChildren, useContext } from "react";
import { Controller, useFieldArray } from "react-hook-form";
import validator from "validator";
import landRegistrationInstructionsPdf from "../../assets/anleitung-katasterdaten.pdf";
import reaInstructionsPdf from "../../assets/anleitung-vwv-nummer.pdf";
import { useMember } from "../../context/memberContext";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../context/notificationContext";
import useToggle from "../../hooks/useToggle";
import { useTranslations } from "../../hooks/useTranslations";
import {
  SP,
  putDac7Member,
  useCountries,
  useDac7Member,
} from "../../http/serviceportalApi";
import SVGAdd from "../../icons/Add.svg?react";
import SVGDelete from "../../icons/Delete.svg?react";
import { getRemoteDataStatus, mergeStatuses } from "../../utils";
import ButtonGroup from "../ui/ButtonGroup";
import ErrorText from "../ui/ErrorText";
import Fieldset from "../ui/Fieldset";
import Form from "../ui/Form";
import FormField from "../ui/FormField";
import FormFieldGrid from "../ui/FormFieldGrid";
import Icon from "../ui/Icon";
import Input from "../ui/Input";
import Page from "../ui/Page";
import Panel from "../ui/Panel";
import PdfPreview from "../ui/PdfPreview";
import Select from "../ui/Select";
import Separator from "../ui/Separator";
import CountrySelect from "./CountrySelect";
import styles from "./Dac7.module.css";

const emptyLandRegistration: SP.Dac7.PutMemberLandRegistration = {
  code: "",
  sheet: "",
  parcel: "",
  subaltern: "",
};

const explicitEmptyValue = "-";

const Dac7 = () => {
  const { i18n } = useTranslations();
  const dispatch = useContext(NotificationDispatch);
  const { member } = useMember();

  const requiredOrDashErrorMessage = i18n.dac7.requiredOrDash;

  const {
    data,
    isValidating: isMemberValidating,
    error: memberError,
    mutate,
  } = useDac7Member(member?.id);
  const memberStatus = getRemoteDataStatus({
    isValidating: isMemberValidating,
    error: memberError,
  });
  const isCompany = data?.type === "company";
  const isSoleProprietorship = data?.type === "soleProprietorship";
  const isPrivatePerson = data?.type === "privatePerson";

  const {
    data: countryListData,
    isValidating: isCountryListValidating,
    error: countryListError,
  } = useCountries();
  const countries = countryListData ?? [];
  const sortedCountries = {
    byAlpha2Code: (countryListData ?? []).sort(byAlpha2Code),
    byName: (countryListData ?? []).sort(byName),
  };
  const countryListStatus = getRemoteDataStatus({
    isValidating: isCountryListValidating,
    error: countryListError,
  });

  const status = mergeStatuses([memberStatus, countryListStatus]);

  if (!member) return null;

  return (
    <Page
      title={i18n.departments.webMarketing.apps.dac7.title}
      description={i18n.departments.webMarketing.apps.dac7.description}
      backLinkText={i18n.dac7.backLink}
      isLoading={status === "validating"}
    >
      {status === "success" ? (
        data?.companies?.every((company) => !company.isReportable) ? (
          <>{i18n.dac7.notReportable}</>
        ) : (
          <Form<SP.Dac7.PutMemberBody>
            defaultValues={{
              fiscalCodeIssuedBy: data?.fiscalCodeIssuedBy ?? "IT",
              fiscalCode: data?.fiscalCode,
              birthDate: data?.birthDate
                ? formatYearMonthDate(new Date(data.birthDate))
                : undefined,
              pec: data?.pec,
              accountOwner: data?.accountOwner,
              iban: data?.iban,
              vat: data?.vat,
              rea: data?.rea,
              permanentEstablishments: data?.permanentEstablishments ?? [],
              address: data?.address,
              companies:
                data?.companies?.map(({ id, address, landRegistrations }) => ({
                  id,
                  address,
                  landRegistrations:
                    landRegistrations && landRegistrations.length > 0
                      ? landRegistrations.map((lr) => ({
                          ...lr,
                          sheet: lr.sheet ?? explicitEmptyValue,
                          subaltern: lr.subaltern ?? explicitEmptyValue,
                        }))
                      : [emptyLandRegistration],
                })) ?? [],
            }}
            onSubmit={() => (formData) => {
              const body: SP.Dac7.PutMemberBody = {
                fiscalCodeIssuedBy: formData.fiscalCodeIssuedBy,
                fiscalCode: formData.fiscalCode.toUpperCase(),
                birthDate:
                  isSoleProprietorship || isPrivatePerson
                    ? formData.birthDate
                    : undefined,
                pec: formData.pec,
                accountOwner: formData.accountOwner,
                iban: formData.iban.toUpperCase(),
                vat:
                  isCompany || isSoleProprietorship
                    ? formData.vat?.toUpperCase()
                    : undefined,
                rea:
                  isCompany || isSoleProprietorship
                    ? formData.rea?.toUpperCase()
                    : undefined,
                permanentEstablishments: isCompany
                  ? formData.permanentEstablishments
                  : [],
                address: formData.address,
                companies: formData.companies.map((c) => ({
                  ...c,
                  landRegistrations: c.landRegistrations.reduce(
                    (state: SP.Dac7.PutMemberLandRegistration[], value) => {
                      if (!value.sheet && !value.subaltern) return state;
                      return [
                        ...state,
                        {
                          ...value,
                          code: value.code.toUpperCase(),
                          sheet:
                            value.sheet === explicitEmptyValue
                              ? undefined
                              : value.sheet,
                          subaltern:
                            value.subaltern === explicitEmptyValue
                              ? undefined
                              : value.subaltern,
                        },
                      ];
                    },
                    [],
                  ),
                })),
              };

              putDac7Member(member.id, body)
                .then((updated) => {
                  mutate(updated, { revalidate: false });
                  dispatch(showSuccessNotification());
                })
                .catch((err) => dispatch(showErrorNotification(err)));
            }}
          >
            {({ control, watch, register, formState: { errors } }) => {
              const fiscalCodeIssuedBy = watch("fiscalCodeIssuedBy");

              return (
                <>
                  <Section title={i18n.dac7.seller.title}>
                    <Panel>
                      <FormField
                        label={i18n.dac7.seller.classification.label}
                        direction="column"
                        helpText={i18n.dac7.seller.classification.helpText}
                      >
                        <ButtonGroup>
                          <ButtonGroup.Button
                            disabled={true}
                            isActive={isCompany}
                          >
                            {i18n.dac7.seller.classification.company}
                          </ButtonGroup.Button>
                          <ButtonGroup.Button
                            disabled={true}
                            isActive={isSoleProprietorship}
                          >
                            {i18n.dac7.seller.classification.soleProprietorship}
                          </ButtonGroup.Button>
                          <ButtonGroup.Button
                            disabled={true}
                            isActive={isPrivatePerson}
                          >
                            {i18n.dac7.seller.classification.privatePerson}
                          </ButtonGroup.Button>
                        </ButtonGroup>
                      </FormField>

                      <Separator />

                      <FormFieldGrid>
                        {isCompany && (
                          <FormField
                            label={i18n.dac7.seller.companyName.label}
                            direction="column"
                            helpText={i18n.dac7.seller.companyName.helpText}
                          >
                            {({ labelId }) => (
                              <Input
                                id={labelId}
                                disabled={true}
                                defaultValue={data?.companyName}
                              />
                            )}
                          </FormField>
                        )}
                        {(isSoleProprietorship || isPrivatePerson) && (
                          <>
                            <FormField
                              label={i18n.dac7.seller.firstName.label}
                              direction="column"
                              helpText={i18n.dac7.seller.firstName.helpText}
                            >
                              {({ labelId }) => (
                                <Input
                                  id={labelId}
                                  disabled={true}
                                  defaultValue={data?.firstName}
                                />
                              )}
                            </FormField>
                            <FormField
                              label={i18n.dac7.seller.lastName.label}
                              direction="column"
                              helpText={i18n.dac7.seller.lastName.helpText}
                            >
                              {({ labelId }) => (
                                <Input
                                  id={labelId}
                                  disabled={true}
                                  defaultValue={data?.lastName}
                                />
                              )}
                            </FormField>
                          </>
                        )}
                        {(isSoleProprietorship || isPrivatePerson) && (
                          <FormField
                            label={i18n.dac7.seller.birthDate}
                            direction="column"
                            error={errors.birthDate}
                          >
                            {({ labelId, isOptional, isInvalid }) => (
                              <Input
                                id={labelId}
                                isInvalid={isInvalid}
                                type="date"
                                {...register("birthDate", {
                                  required: !isOptional,
                                })}
                              />
                            )}
                          </FormField>
                        )}
                        <FormField
                          label={i18n.dac7.seller.pec}
                          direction="column"
                          error={errors.pec}
                        >
                          {({ labelId, isOptional, isInvalid }) => (
                            <Input
                              id={labelId}
                              isInvalid={isInvalid}
                              inputMode="email"
                              autoComplete="email"
                              {...register("pec", {
                                required: !isOptional,
                                validate: (v) => validator.isEmail(v),
                              })}
                            />
                          )}
                        </FormField>
                        <FormField
                          label={i18n.dac7.seller.taxId.label}
                          direction="column"
                          error={errors.fiscalCode}
                          helpText={i18n.dac7.seller.taxId.helpText}
                        >
                          {({ labelId, isOptional, isInvalid }) => (
                            <div className={styles.fiscalCode}>
                              <Select {...register("fiscalCodeIssuedBy")}>
                                {sortedCountries.byAlpha2Code.map((c) => (
                                  <option
                                    key={c.alpha2Code}
                                    value={c.alpha2Code}
                                  >
                                    {c.alpha2Code}
                                  </option>
                                ))}
                              </Select>
                              <Input
                                id={labelId}
                                className={styles.uppercase}
                                isInvalid={isInvalid}
                                {...register("fiscalCode", {
                                  required: !isOptional,
                                  validate: (v) => {
                                    try {
                                      const locale = countries.find(
                                        (c) =>
                                          c.alpha2Code === fiscalCodeIssuedBy,
                                      )?.locale;
                                      return (
                                        !locale ||
                                        validator.isTaxID(v, locale) ||
                                        validator.isVAT(
                                          fiscalCodeIssuedBy + v,
                                          fiscalCodeIssuedBy as Parameters<
                                            typeof validator.isVAT
                                          >[1],
                                        )
                                      );
                                    } catch {
                                      // Skip validation if the selected country/locale is not supported
                                      return true;
                                    }
                                  },
                                })}
                              />
                            </div>
                          )}
                        </FormField>
                        {(isCompany || isSoleProprietorship) && (
                          <>
                            <FormField
                              label={i18n.dac7.seller.vatId.label}
                              direction="column"
                              error={errors.vat}
                              helpText={i18n.dac7.seller.vatId.helpText}
                            >
                              {({ labelId, isOptional, isInvalid }) => (
                                <Input
                                  id={labelId}
                                  className={styles.uppercase}
                                  isInvalid={isInvalid}
                                  {...register("vat", {
                                    required: !isOptional,
                                    pattern: /^[A-Z]{2}/i,
                                    // Only Italian VAT codes are allowed
                                    validate: (v) => {
                                      try {
                                        return (
                                          v &&
                                          (validator.isTaxID(v, "it-IT") ||
                                            validator.isVAT(v, "IT"))
                                        );
                                      } catch {
                                        // Skip validation if the selected country/locale is not supported
                                        return true;
                                      }
                                    },
                                  })}
                                />
                              )}
                            </FormField>
                            <FormField
                              label={i18n.dac7.seller.reaNumber.label}
                              direction="column"
                              isOptional={isSoleProprietorship}
                              error={errors.rea}
                              helpText={
                                <>
                                  {i18n.dac7.seller.reaNumber.helpText(
                                    <PdfLink
                                      href={reaInstructionsPdf}
                                      title={
                                        i18n.dac7.seller.reaNumber
                                          .instructionsPdfTitle
                                      }
                                    >
                                      {
                                        i18n.dac7.seller.reaNumber
                                          .helpTextLinkLabel
                                      }
                                    </PdfLink>,
                                  )}
                                </>
                              }
                            >
                              {({ labelId, isOptional, isInvalid }) => (
                                <Input
                                  id={labelId}
                                  className={styles.uppercase}
                                  isInvalid={isInvalid}
                                  {...register("rea", {
                                    required: !isOptional,
                                    pattern: /(^[a-zA-Z]{2}-[0-9]+$)/,
                                  })}
                                />
                              )}
                            </FormField>
                          </>
                        )}
                      </FormFieldGrid>

                      <Separator />

                      <Fieldset title={i18n.dac7.seller.address}>
                        <FormFieldGrid>
                          <FormField
                            label={i18n.dac7.seller.street}
                            direction="column"
                            error={errors.address?.street}
                          >
                            {({ labelId, isInvalid }) => (
                              <Input
                                id={labelId}
                                isInvalid={isInvalid}
                                {...register("address.street")}
                              />
                            )}
                          </FormField>
                          <FormField
                            label={i18n.dac7.seller.buildingNumber}
                            direction="column"
                            error={errors.address?.buildingIdentifier}
                          >
                            {({ labelId, isInvalid }) => (
                              <Input
                                id={labelId}
                                isInvalid={isInvalid}
                                {...register("address.buildingIdentifier")}
                              />
                            )}
                          </FormField>
                          <FormField
                            label={i18n.dac7.seller.postalCode}
                            direction="column"
                            error={errors.address?.zip}
                          >
                            {({ labelId, isOptional, isInvalid }) => (
                              <Input
                                id={labelId}
                                isInvalid={isInvalid}
                                {...register("address.zip", {
                                  required: !isOptional,
                                })}
                              />
                            )}
                          </FormField>
                          <FormField
                            label={i18n.dac7.seller.municipality}
                            direction="column"
                            error={errors.address?.city}
                          >
                            {({ labelId, isOptional, isInvalid }) => (
                              <Input
                                id={labelId}
                                isInvalid={isInvalid}
                                {...register("address.city", {
                                  required: !isOptional,
                                })}
                              />
                            )}
                          </FormField>
                          <FormField
                            label={i18n.dac7.seller.province}
                            direction="column"
                            error={errors.address?.province}
                          >
                            {({ labelId, isOptional, isInvalid }) => (
                              <Input
                                id={labelId}
                                isInvalid={isInvalid}
                                {...register("address.province", {
                                  required: !isOptional,
                                })}
                              />
                            )}
                          </FormField>
                          <FormField
                            label={i18n.dac7.seller.country}
                            direction="column"
                            error={errors.address?.country}
                          >
                            {({ labelId, isOptional, isInvalid }) => (
                              <Select
                                id={labelId}
                                className={styles.addressCountrySelect}
                                isInvalid={isInvalid}
                                {...register("address.country", {
                                  required: !isOptional,
                                })}
                              >
                                {sortedCountries.byName.map((c) => (
                                  <option
                                    key={c.alpha2Code}
                                    value={c.alpha2Code}
                                  >
                                    {c.name}
                                  </option>
                                ))}
                              </Select>
                            )}
                          </FormField>
                        </FormFieldGrid>
                      </Fieldset>

                      <Separator />

                      <Fieldset title={i18n.dac7.seller.bankData}>
                        <FormFieldGrid>
                          <FormField
                            label={i18n.dac7.seller.accountOwner}
                            direction="column"
                            error={errors.accountOwner}
                          >
                            {({ labelId, isOptional, isInvalid }) => (
                              <Input
                                id={labelId}
                                isInvalid={isInvalid}
                                {...register("accountOwner", {
                                  required: !isOptional,
                                })}
                              />
                            )}
                          </FormField>
                          <FormField
                            label={i18n.dac7.seller.iban}
                            direction="column"
                            isOptional={isSoleProprietorship}
                            error={errors.iban}
                          >
                            {({ labelId, isOptional, isInvalid }) => (
                              <Input
                                id={labelId}
                                className={styles.uppercase}
                                isInvalid={isInvalid}
                                {...register("iban", {
                                  required: !isOptional,
                                  validate: (v) => !v || validator.isIBAN(v),
                                })}
                              />
                            )}
                          </FormField>
                        </FormFieldGrid>
                      </Fieldset>

                      {isCompany && (
                        <>
                          <Separator />

                          <Fieldset
                            title={
                              i18n.dac7.seller.permanentEstablishments.title
                            }
                            description={
                              i18n.dac7.seller.permanentEstablishments
                                .description
                            }
                          >
                            <Controller
                              name="permanentEstablishments"
                              control={control}
                              rules={{
                                validate: (v) =>
                                  v.length > 0 ||
                                  i18n.dac7.seller.permanentEstablishments
                                    .minOneCountry,
                              }}
                              render={({ field, fieldState: { error } }) => (
                                <CountrySelect
                                  ref={field.ref}
                                  countries={countries.filter(
                                    (c) => c.isEuMember,
                                  )}
                                  selectedItems={field.value}
                                  error={error}
                                  onChange={field.onChange}
                                />
                              )}
                            />
                          </Fieldset>
                        </>
                      )}
                    </Panel>
                  </Section>

                  <Section
                    title={i18n.dac7.companies.title}
                    description={i18n.dac7.companies.description}
                  >
                    {data?.companies?.map((c, i) => {
                      const { fields, append, remove } = useFieldArray({
                        name: `companies.${i}.landRegistrations`,
                        control,
                        keyName: "fieldId",
                      });
                      const companyErrors = errors.companies?.[i];

                      return (
                        <div key={c.id} className={styles.company}>
                          <h3 className={styles.companyName}>{c.name}</h3>
                          {!c.isReportable && (
                            <div className={styles.subtitle}>
                              {i18n.dac7.companies.notReportable}
                            </div>
                          )}
                          <Panel>
                            <Fieldset title={i18n.dac7.companies.address}>
                              <FormFieldGrid>
                                <FormField
                                  label={i18n.dac7.companies.street}
                                  direction="column"
                                  error={companyErrors?.address?.street}
                                >
                                  {({ labelId, isInvalid }) => (
                                    <Input
                                      id={labelId}
                                      isInvalid={isInvalid}
                                      {...register(
                                        `companies.${i}.address.street`,
                                      )}
                                    />
                                  )}
                                </FormField>
                                <FormField
                                  label={i18n.dac7.companies.buildingNumber}
                                  direction="column"
                                  error={
                                    companyErrors?.address?.buildingIdentifier
                                  }
                                >
                                  {({ labelId, isInvalid }) => (
                                    <Input
                                      id={labelId}
                                      isInvalid={isInvalid}
                                      {...register(
                                        `companies.${i}.address.buildingIdentifier`,
                                      )}
                                    />
                                  )}
                                </FormField>
                                <FormField
                                  label={i18n.dac7.companies.postalCode}
                                  direction="column"
                                  error={companyErrors?.address?.zip}
                                >
                                  {({ labelId, isOptional, isInvalid }) => (
                                    <Input
                                      id={labelId}
                                      isInvalid={isInvalid}
                                      {...register(
                                        `companies.${i}.address.zip`,
                                        {
                                          required: !isOptional,
                                        },
                                      )}
                                    />
                                  )}
                                </FormField>
                                <FormField
                                  label={i18n.dac7.companies.municipality}
                                  direction="column"
                                  error={companyErrors?.address?.city}
                                >
                                  {({ labelId, isOptional, isInvalid }) => (
                                    <Input
                                      id={labelId}
                                      isInvalid={isInvalid}
                                      {...register(
                                        `companies.${i}.address.city`,
                                        {
                                          required: !isOptional,
                                        },
                                      )}
                                    />
                                  )}
                                </FormField>
                                <FormField
                                  label={i18n.dac7.companies.province}
                                  direction="column"
                                  error={companyErrors?.address?.province}
                                >
                                  {({ labelId, isOptional, isInvalid }) => (
                                    <Input
                                      id={labelId}
                                      isInvalid={isInvalid}
                                      {...register(
                                        `companies.${i}.address.province`,
                                        {
                                          required: !isOptional,
                                        },
                                      )}
                                    />
                                  )}
                                </FormField>
                                <FormField
                                  label={i18n.dac7.companies.country}
                                  direction="column"
                                  error={companyErrors?.address?.country}
                                >
                                  {({ labelId, isOptional, isInvalid }) => (
                                    <Select
                                      id={labelId}
                                      className={styles.addressCountrySelect}
                                      isInvalid={isInvalid}
                                      {...register(
                                        `companies.${i}.address.country`,
                                        {
                                          required: !isOptional,
                                        },
                                      )}
                                    >
                                      {sortedCountries.byName.map((c) => (
                                        <option
                                          key={c.alpha2Code}
                                          value={c.alpha2Code}
                                        >
                                          {c.name}
                                        </option>
                                      ))}
                                    </Select>
                                  )}
                                </FormField>
                              </FormFieldGrid>
                            </Fieldset>

                            <Separator />

                            <Fieldset
                              title={i18n.dac7.companies.landRegistration.title}
                              description={
                                <>
                                  {i18n.dac7.companies.landRegistration.description(
                                    <PdfLink
                                      href={landRegistrationInstructionsPdf}
                                      title={
                                        i18n.dac7.companies.landRegistration
                                          .instructionsPdfTitle
                                      }
                                    >
                                      {
                                        i18n.dac7.companies.landRegistration
                                          .descriptionLinkLabel
                                      }
                                    </PdfLink>,
                                  )}
                                </>
                              }
                            >
                              {fields.length > 0 && (
                                <div
                                  className={
                                    styles.landRegistrationsGridWrapper
                                  }
                                >
                                  <div className={styles.landRegistrationsGrid}>
                                    <LandRegistrationHeader
                                      title={
                                        i18n.dac7.companies.landRegistration
                                          .municipalityCode.title
                                      }
                                      subtitle={
                                        i18n.dac7.companies.landRegistration
                                          .municipalityCode.subtitle
                                      }
                                    />
                                    <LandRegistrationHeader
                                      title={
                                        i18n.dac7.companies.landRegistration
                                          .sheet.title
                                      }
                                      subtitle={
                                        i18n.dac7.companies.landRegistration
                                          .sheet.subtitle
                                      }
                                    />
                                    <LandRegistrationHeader
                                      title={
                                        i18n.dac7.companies.landRegistration
                                          .parcel.title
                                      }
                                      subtitle={
                                        i18n.dac7.companies.landRegistration
                                          .parcel.subtitle
                                      }
                                    />
                                    <LandRegistrationHeader
                                      title={
                                        i18n.dac7.companies.landRegistration
                                          .subunit.title
                                      }
                                      subtitle={
                                        i18n.dac7.companies.landRegistration
                                          .subunit.subtitle
                                      }
                                    />
                                    <div></div>

                                    {fields.map((lr, j) => {
                                      const landRegistrationErrors =
                                        companyErrors?.landRegistrations?.[j];

                                      return (
                                        <Fragment key={lr.fieldId}>
                                          <FormField
                                            error={landRegistrationErrors?.code}
                                          >
                                            {({ isInvalid }) => (
                                              <Input
                                                className={styles.uppercase}
                                                isInvalid={isInvalid}
                                                {...register(
                                                  `companies.${i}.landRegistrations.${j}.code`,
                                                  {
                                                    required: true,
                                                    pattern:
                                                      /^[A-Z]{1}[0-9]{3}$/i,
                                                  },
                                                )}
                                              />
                                            )}
                                          </FormField>
                                          <FormField
                                            error={
                                              landRegistrationErrors?.sheet
                                            }
                                          >
                                            {({ isInvalid }) => (
                                              <Input
                                                isInvalid={isInvalid}
                                                {...register(
                                                  `companies.${i}.landRegistrations.${j}.sheet`,
                                                  {
                                                    required:
                                                      requiredOrDashErrorMessage,
                                                    pattern:
                                                      /^(-|[0-9]+(-[0-9]+)?)$/,
                                                  },
                                                )}
                                              />
                                            )}
                                          </FormField>
                                          <FormField
                                            error={
                                              landRegistrationErrors?.parcel
                                            }
                                          >
                                            {({ isInvalid }) => (
                                              <Input
                                                isInvalid={isInvalid}
                                                {...register(
                                                  `companies.${i}.landRegistrations.${j}.parcel`,
                                                  {
                                                    required: true,
                                                    pattern: /^\d+(\/\d+)*$/,
                                                  },
                                                )}
                                              />
                                            )}
                                          </FormField>
                                          <FormField
                                            error={
                                              landRegistrationErrors?.subaltern
                                            }
                                          >
                                            {({ isInvalid }) => (
                                              <Input
                                                isInvalid={isInvalid}
                                                {...register(
                                                  `companies.${i}.landRegistrations.${j}.subaltern`,
                                                  {
                                                    required:
                                                      requiredOrDashErrorMessage,
                                                    pattern: /^(-|[0-9]+)$/,
                                                  },
                                                )}
                                              />
                                            )}
                                          </FormField>
                                          <button
                                            className={styles.removeEntryButton}
                                            type="button"
                                            disabled={fields.length === 1}
                                            onClick={() => remove(j)}
                                          >
                                            <Icon
                                              className={styles.deleteIcon}
                                              glyph={SVGDelete}
                                            />
                                          </button>
                                        </Fragment>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                              <button
                                className={styles.addEntryButton}
                                type="button"
                                onClick={() => append(emptyLandRegistration)}
                              >
                                <Icon
                                  className={styles.addIcon}
                                  glyph={SVGAdd}
                                />
                                {i18n.dac7.companies.landRegistration.addEntry}
                              </button>
                            </Fieldset>
                          </Panel>
                        </div>
                      );
                    })}
                  </Section>
                </>
              );
            }}
          </Form>
        )
      ) : null}

      {status === "failure" && (
        <div className={styles.status}>
          {memberError?.status === 404 ? (
            <>
              {i18n.dac7.errors.configIncomplete.line1}
              <br />
              {i18n.dac7.errors.configIncomplete.line2}
            </>
          ) : (
            <ErrorText text={i18n.dac7.errors.loadError} />
          )}
        </div>
      )}
    </Page>
  );
};

type SectionProps = PropsWithChildren<{
  title: string;
  description?: string;
}>;

const Section = ({ title, description, children }: SectionProps) => (
  <section className={styles.section}>
    <h2>{title}</h2>
    {description && <p>{description}</p>}
    {children}
  </section>
);

interface LandRegistrationHeaderProps {
  title: string;
  subtitle: string;
}

const LandRegistrationHeader = ({
  title,
  subtitle,
}: LandRegistrationHeaderProps) => (
  <div>
    <div className={styles.title}>{title}</div>
    <div className={styles.subtitle}>{subtitle}</div>
  </div>
);

const formatYearMonthDate = (date: Date) => {
  const year = String(date.getFullYear());
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const byAlpha2Code = (a: SP.Country, b: SP.Country) =>
  a.alpha2Code.localeCompare(b.alpha2Code);

const byName = (a: SP.Country, b: SP.Country) => a.name.localeCompare(b.name);

type PdfLinkProps = PropsWithChildren<{
  href: string;
  title: string;
}>;

const PdfLink = ({ children, href, title }: PdfLinkProps) => {
  const modal = useToggle();

  return (
    <>
      <a
        href={href}
        target="_blank"
        onClick={(e) => {
          e.preventDefault();
          modal.open();
        }}
      >
        {children}
      </a>
      <PdfPreview
        isOpen={modal.isOpen}
        onClose={modal.close}
        title={title}
        src={href}
      />
    </>
  );
};

export default Dac7;
